import { GTMPush } from '@utilities/gtm';
import { sha256 } from '@utilities/sha-256';
import md5 from 'md5';

import { SubscriptionStoreAddress, SubscriptionStoreUser } from '../store.types';
import {
    areaNewsletterBaseApiCall,
    NewsletterFormAreaInfo,
    NewsletterFormBuyerInfo,
} from './base-api-call';

interface AreaProjectNewsletterProps {
    areaInfo: NewsletterFormAreaInfo;
    userInfo: SubscriptionStoreUser;
    buyerInfo?: NewsletterFormBuyerInfo;
    GTMEventLabel?: string;
}

interface ProjectPhaseNewsletterProps {
    areaInfo: NewsletterFormAreaInfo & {
        districtId: string;
        projectPhaseId: string;
    };
    address?: SubscriptionStoreAddress;
    userInfo: SubscriptionStoreUser;
    buyerInfo?: NewsletterFormBuyerInfo;
    GTMEventLabel?: string;
}

interface PropertyTypeNewsletterProps {
    areaInfo: NewsletterFormAreaInfo & {
        districtId: string;
        projectPhaseId: string;
        propertyTypeId: string;
    };
    userInfo: SubscriptionStoreUser;
    address: SubscriptionStoreAddress;
    buyerInfo?: NewsletterFormBuyerInfo;
    GTMEventLabel?: string;
}

interface ConstructionNumberNewsletterProps {
    areaInfo: NewsletterFormAreaInfo & {
        districtId: string;
        projectPhaseId: string;
        propertyTypeId: string;
        constructionNumberId: string;
    };
    userInfo: SubscriptionStoreUser;
    address: SubscriptionStoreAddress;
    buyerInfo?: NewsletterFormBuyerInfo;
    GTMEventLabel?: string;
}

const areaNewsletterApiCalls = {
    constructionNumber: async ({
        areaInfo,
        userInfo,
        address,
        buyerInfo,
        GTMEventLabel,
    }: ConstructionNumberNewsletterProps) => {
        try {
            await areaNewsletterBaseApiCall({
                areaInfo,
                userInfo,
                address,
                buyerInfo,
            });

            if (GTMEventLabel) {
                GTMPush({
                    event: 'gtm.NewsletterSubscription',
                    eventCategory: 'Newsletter',
                    eventAction: 'Subscribe Bouwnummer',
                    eventLabel: `${areaInfo.constructionNumberId} ${GTMEventLabel}`,
                    email: md5(userInfo.email),
                    email_sha_256: await sha256(userInfo.email),
                });
            }
        } catch (error) {
            console.log('Error subscribing to property type newsletter', error);
        }
    },

    propertyType: async ({
        areaInfo,
        userInfo,
        address,
        buyerInfo,
        GTMEventLabel,
    }: PropertyTypeNewsletterProps) => {
        try {
            await areaNewsletterBaseApiCall({
                areaInfo,
                userInfo,
                address,
                buyerInfo,
            });

            if (GTMEventLabel) {
                GTMPush({
                    event: 'gtm.NewsletterSubscription',
                    eventCategory: 'Newsletter',
                    eventAction: 'Subscribe Woningtype',
                    eventLabel: `${areaInfo.propertyTypeId} ${GTMEventLabel}`,
                    email: md5(userInfo.email),
                    email_sha_256: await sha256(userInfo.email),
                });
            }
        } catch (error) {
            console.log('Error subscribing to property type newsletter', error);
        }
    },

    projectPhase: async ({
        areaInfo,
        userInfo,
        address,
        buyerInfo,
        GTMEventLabel,
    }: ProjectPhaseNewsletterProps) => {
        try {
            await areaNewsletterBaseApiCall({
                areaInfo,
                userInfo,
                address,
                buyerInfo,
            });

            if (GTMEventLabel) {
                GTMPush({
                    event: 'gtm.NewsletterSubscription',
                    eventCategory: 'Newsletter',
                    eventAction: 'Subscribe Gebied',
                    eventLabel: GTMEventLabel,
                    email: md5(userInfo.email),
                    email_sha_256: await sha256(userInfo.email),
                });
            }
        } catch (error) {
            console.log('Error subscribing to project phase newsletter', error);
        }
    },

    areaProject: async ({
        areaInfo,
        userInfo,
        buyerInfo,
        GTMEventLabel,
    }: AreaProjectNewsletterProps) => {
        try {
            await areaNewsletterBaseApiCall({
                areaInfo,
                userInfo,
                buyerInfo,
            });

            if (GTMEventLabel) {
                GTMPush({
                    event: 'gtm.NewsletterSubscription',
                    eventCategory: 'Newsletter',
                    eventAction: 'Subscribe Gebied',
                    eventLabel: GTMEventLabel,
                    email: md5(userInfo.email),
                    email_sha_256: await sha256(userInfo.email),
                });
            }
        } catch (error) {
            console.log('Error subscribing to project phase newsletter', error);
        }
    },
};

export {
    AreaProjectNewsletterProps,
    ProjectPhaseNewsletterProps,
    PropertyTypeNewsletterProps,
    ConstructionNumberNewsletterProps,
    areaNewsletterApiCalls,
};
